import { getToolOrDynamicToolName, isToolUIPart } from 'ai'
import type { DynamicToolUIPart, ToolUIPart, UIMessage } from 'ai'
import {
  isConfirmTool,
} from '@llj/ai-service-interface/dist/entities/tool-catalogue.interface'
import { EXECUTE_TIMEOUT_RESULT } from '../../tools/pause/confirm/result'
import type { AbandonReason, ConfirmStatus } from '../../tools/pause/confirm/result'

// 確認卡的純邏輯（#1693）。哪支工具要確認一律讀工具目錄，不寫死工具名；
// 卡片狀態只看 output 的 status，即時串流與重新整理後還原的是同一種形狀。

type MessagePart = UIMessage['parts'][number]
type AnyToolPart = ToolUIPart | DynamicToolUIPart

// 卡片上看得到的六態；pending 是等你按確認，其餘五態都已不能再按
export type ConfirmCardStatus = 'pending' | Exclude<ConfirmStatus, 'awaiting_confirmation'>

const CARD_STATUSES: readonly string[] = [
  'awaiting_confirmation', 'executing', 'executed', 'failed', 'cancelled', 'interrupted',
]

const outputOf = (part: AnyToolPart): Record<string, unknown> | null =>
  (part.state === 'output-available' && typeof part.output === 'object' && part.output !== null ?
    part.output as Record<string, unknown> : null)

export const toolNameOf = (part: AnyToolPart): string => getToolOrDynamicToolName(part)

// 要不要畫成確認卡。prepare 還在跑、回需要補充資訊、同一步被作廢的那幾份都不是，
// 照一般工具摺疊列畫
export const isConfirmCardPart = (part: MessagePart): part is AnyToolPart => {
  if (!isToolUIPart(part) || !isConfirmTool(getToolOrDynamicToolName(part))) return false
  const status = outputOf(part)?.status
  return typeof status === 'string' && CARD_STATUSES.includes(status)
}

/** 卡片狀態。confirming 是本機剛按下確認、伺服器還沒回結果的那一張 */
export const confirmCardStatus = (part: AnyToolPart, confirming: boolean): ConfirmCardStatus => {
  const status = outputOf(part)?.status
  if (status === 'awaiting_confirmation') return confirming ? 'executing' : 'pending'
  return (typeof status === 'string' && CARD_STATUSES.includes(status) ?
    status : 'failed') as ConfirmCardStatus
}

/** 卡片要畫的東西：prepare 給的 display、結果與失敗訊息。讀不到的欄位當沒有 */
export const readConfirmOutput = (part: AnyToolPart) => {
  const output = outputOf(part) ?? {}
  return {
    display: output.display,
    result: output.result,
    message: typeof output.message === 'string' ? output.message : '',
    error: typeof output.error === 'string' ? output.error : '',
  }
}

/** 失敗但結果不確定：逾時時呼叫可能仍在背後完成，卡片請使用者到後台查看 */
export const isUncertainOutcome = (error: string): boolean => error === EXECUTE_TIMEOUT_RESULT.error

/** 目前等你確認的那一張；只看最後一則助理訊息，等待中不能開新的一輪 */
export const findPendingConfirmPart = (messages: UIMessage[]): AnyToolPart | null => {
  const last = messages[messages.length - 1]
  if (!last || last.role !== 'assistant') return null
  const pending = last.parts.filter((part) =>
    isConfirmCardPart(part) && confirmCardStatus(part, false) === 'pending')
  const part = pending[pending.length - 1]
  return part && isToolUIPart(part) ? part : null
}

/** 確認續跑請求的 body：只送 toolCallId 與決定，參數一律以伺服器事件流為準 */
export const confirmRequestBody = (conversationId: string | null, toolCallId: string) => ({
  conversationId, toolCallId, decision: 'confirm' as const,
})

/** 放棄後就地顯示的 output：伺服器寫給模型的那一份加上原本的 display */
export const abandonedOutput = (part: AnyToolPart, result: { status: AbandonReason; message: string }) =>
  ({ ...result, display: readConfirmOutput(part).display })

/** 這一次 /ai/chat 請求是不是確認續跑；錯誤處理據此給確認專屬的說法 */
export const isConfirmRequest = (init?: RequestInit): boolean => {
  if (typeof init?.body !== 'string') return false
  try {
    return (JSON.parse(init.body) as { decision?: unknown }).decision === 'confirm'
  } catch {
    return false
  }
}
